import React, { useEffect, useMemo, useRef, useState } from 'react';
import { IKpiVessel, TPolygonCalls, TPolygonCallsItem } from '../../../kpiTypes';
import { AgGridReact } from 'ag-grid-react'; // React Data Grid Component
import { themeBalham } from 'ag-grid-enterprise';
import { ColDef } from 'ag-grid-community';
import LoadingSpinner from '../../../../../shared/components/loading-spinner/LoadingSpinner';
import MapLibre from '../../../../../shared/components/maps/map-libre/MapLibre';
import { formatValueAsString } from '../../../../../utils/helpers';
import { useAppDispatch, useAppSelector } from '../../../../../core/rootHooks';
import { AppDispatch, RootState } from '../../../../../core/store';
import { TVesselInfo } from '../../../../main-map/mapTypes';
import { getVesselLastPortCallsRequestAction } from '../../../../main-map/slices/vesselLastPortCallsSlice';
import { Layer, Source } from 'react-map-gl';
import { portCallsLayerStyle } from '../../../../../shared/components/maps/map-libre/mapLayers';
import ShipyardVisitsMapLegend from './ShipyardVisitsMapLegend';
import ShipyardVisitsChart from './ShipyardVisitsChart';
import ExpandableContainer from '../../../../../shared/components/expandable-container/ExpandableContainer';
import { findCenterPoint } from '../../../../../utils/mapHelpers';
import { selectFeatureDateRangeAnalysisDateRange } from '../../../../date-range/slices/dateRangeSlice';

export interface IShipyardVisitsProps {
	vessel: IKpiVessel;
	vesselPolygonCalls: TPolygonCalls | null;
}

const ShipyardVisits: React.FC<IShipyardVisitsProps> = ({ ...props }) => {
	const dispatch = useAppDispatch<AppDispatch>();
	const { vesselLastPortCallsData } = useAppSelector((state: RootState) => state.vesselLastPortCallsSlice);
	const analysisDateRange = useAppSelector(selectFeatureDateRangeAnalysisDateRange);

	const [shipyardVisits, setshipyardVisits] = useState<TVesselInfo>();

	const gridColDefs: ColDef<TPolygonCallsItem>[] = [
		{
			headerName: 'Port Call Id',
			field: 'id',
		},
		{
			headerName: 'Start Time',
			field: 'startTime',
			valueFormatter: (params) => (params.value ? new Date(params.value).toLocaleString() : ''),
		},
		{
			headerName: 'End Time',
			field: 'endTime',
			valueFormatter: (params) => (params.value ? new Date(params.value).toLocaleString() : ''),
		},
		{ headerName: 'Port', field: 'portName' },
		{ headerName: 'Port Activity', field: 'portActivity' },
		{
			headerName: 'Total Time In Port',
			field: 'totalTimeInPort',
			valueFormatter: (params) => formatValueAsString(params.value, { minimumFractionDigits: 0, maximumFractionDigits: 2 }),
		},
		{
			headerName: 'Time At Anchor',
			field: 'timeAtAnchor',
			valueFormatter: (params) => formatValueAsString(params.value, { minimumFractionDigits: 0, maximumFractionDigits: 2 }),
		},
		{
			headerName: 'Time Moored',
			field: 'timeMoored',
			valueFormatter: (params) => formatValueAsString(params.value, { minimumFractionDigits: 0, maximumFractionDigits: 2 }),
		},
		{
			headerName: 'Time Maneouvring',
			field: 'timeManeouvring',
			valueFormatter: (params) => formatValueAsString(params.value, { minimumFractionDigits: 0, maximumFractionDigits: 2 }),
		},
		{
			headerName: 'Time Unclassified',
			field: 'timeUnclassified',
			valueFormatter: (params) => formatValueAsString(params.value, { minimumFractionDigits: 0, maximumFractionDigits: 2 }),
		},
	];

	const parsedData = useMemo(() => {
		const dataList = props.vesselPolygonCalls?.dataList?.filter((p) => p.portActivity === 'In Shipyard') || [];

		return Array.isArray(dataList)
			? dataList.map((item: TPolygonCallsItem) => {
					return {
						...item,
						chartTime: new Date(item.startTime), // Ensure each signalDate is a Date object
					};
				})
			: [];
	}, [props.vesselPolygonCalls?.dataList]);

	const ref = useRef<HTMLDivElement>(null);

	const [panelOffset, setPanelOffset] = useState<{ top: number; left: number }>();

	useEffect(() => {
		const updatePanelSize = () => {
			if (ref.current) {
				const { offsetTop, offsetLeft } = ref.current;
				setPanelOffset({ top: offsetTop, left: offsetLeft });
			}
		};

		// Initial measurement
		updatePanelSize();

		// Update on window resize
		window.addEventListener('resize', updatePanelSize);
		return () => {
			window.removeEventListener('resize', updatePanelSize);
		};
	}, []);

	useEffect(() => {
		if (props.vessel) {
			const startDate = analysisDateRange.dateRange!.start.toDate('UTC').toISOString();
			const endDate = analysisDateRange.dateRange!.end.toDate('UTC').toISOString();
			dispatch(getVesselLastPortCallsRequestAction({ imoNumber: props.vessel.imoNumber, startDate, endDate }));
		}
	}, [dispatch, props.vessel, analysisDateRange]);

	useEffect(() => {
		if (vesselLastPortCallsData && vesselLastPortCallsData.features) {
			const shipyardPortCalls = vesselLastPortCallsData.features.filter(
				(item) => item.properties?.portActivity === 'In Shipyard',
			);
			if (shipyardPortCalls && shipyardPortCalls.length > 0) {
				const filteredShipyardVisits: TVesselInfo = {
					type: 'FeatureCollection',
					features: shipyardPortCalls,
				};
				setshipyardVisits(filteredShipyardVisits);
			}
		}
	}, [vesselLastPortCallsData]);

	return (
		<>
			{parsedData === null || !parsedData || parsedData.length === 0 ? (
				'No shipyard visits'
			) : (
				<div ref={ref} className="flex flex-col gap-4 w-full">
					<div className="flex flex-row gap-4 basis-1/2">
						<div className="flex flex-col w-full basis-1/3 border border-msi-darkgrey-color-300 rounded p-1">
							<ShipyardVisitsMapLegend />
							{shipyardVisits && (
								<MapLibre center={findCenterPoint(shipyardVisits)} loadChildrenDirectly>
									<Source id="portCalls" type="geojson" data={shipyardVisits}>
										<Layer {...portCallsLayerStyle()} type="circle" />
									</Source>
								</MapLibre>
							)}
						</div>
						<div className="basis-2/3 w-full border border-msi-darkgrey-color-300 rounded">
							{!props.vesselPolygonCalls || props.vesselPolygonCalls?.loading ? (
								<LoadingSpinner size="md" spinnerClassName="p-2" />
							) : (
								<ExpandableContainer panelOffsets={panelOffset}>
									<ShipyardVisitsChart data={props.vesselPolygonCalls?.dataList || []} />
								</ExpandableContainer>
							)}
						</div>
					</div>
					<div className="flex flex-col gap-4 basis-1/2  border border-msi-darkgrey-color-300 rounded p-1">
						<ExpandableContainer panelOffsets={panelOffset}>
							<AgGridReact
								key={props.vessel.imoNumber}
								columnDefs={gridColDefs}
								rowData={parsedData}
								className="w-full h-full"
								theme={themeBalham}
							/>
						</ExpandableContainer>
					</div>
				</div>
			)}
		</>
	);
};

export default ShipyardVisits;
